import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, RefreshCw, Filter } from 'lucide-react';
import { WaitlistEntry, WaitlistStatsResponse } from '../types';
import { waitlistApi } from '../services/api';
import WaitlistStatsCard from './WaitlistStatsCard';
import WaitlistEntryRow from './WaitlistEntryRow';
import WaitlistTableSkeleton from './WaitlistTableSkeleton';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

/**
 * WaitlistManager - Main page for fair managers to view and manage waitlist
 * Shows statistics and list of all waitlist entries for a fair
 */
function WaitlistManager() {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();

  const [entries, setEntries] = useState<(WaitlistEntry & { vendor_business_name?: string; vendor_email?: string })[]>([]);
  const [stats, setStats] = useState<WaitlistStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const pageSize = 50;

  const loadData = useCallback(async (isRefresh = false) => {
    if (!fairId) return;

    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      // Load waitlist entries and stats in parallel
      const [entriesResponse, statsResponse] = await Promise.all([
        waitlistApi.getFairWaitlist(Number(fairId), page, pageSize),
        waitlistApi.getFairWaitlistStats(Number(fairId))
      ]);

      setEntries(entriesResponse.items as (WaitlistEntry & { vendor_business_name?: string; vendor_email?: string })[]);
      setTotalPages(entriesResponse.total_pages);
      setStats(statsResponse);
    } catch (err: unknown) {
      const message = extractApiError(err).message;
      setError(message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [fairId, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    loadData(true);
  };

  const handleEntryUpdate = () => {
    loadData(true);
  };

  const handleEntryRemove = () => {
    loadData(true);
  };

  const filteredEntries = statusFilter === 'all'
    ? entries
    : entries.filter(e => e.status === statusFilter);

  if (isLoading && !stats) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Waitlist Management</h1>
              <p className="text-gray-600">Loading waitlist data...</p>
            </div>
          </div>
        </div>
        <WaitlistTableSkeleton rows={5} />
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center" data-testid="waitlist-error-message">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Waitlist</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            data-testid="waitlist-try-again-button"
            onClick={() => loadData()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            data-testid="waitlist-back-button"
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Waitlist Management</h1>
            {stats && <p className="text-gray-600" data-testid="fair-name">{stats.fair_name}</p>}
          </div>
        </div>
        <button
          data-testid="refresh-waitlist-button"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="mb-6">
          <WaitlistStatsCard stats={stats} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="waitlist-filters">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filter by status:</span>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="filter-all-button"
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 text-sm rounded-md ${
                statusFilter === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({entries.length})
            </button>
            <button
              data-testid="filter-waiting-button"
              onClick={() => setStatusFilter('waiting')}
              className={`px-3 py-1 text-sm rounded-md ${
                statusFilter === 'waiting'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Waiting ({entries.filter(e => e.status === 'waiting').length})
            </button>
            <button
              data-testid="filter-notified-button"
              onClick={() => setStatusFilter('notified')}
              className={`px-3 py-1 text-sm rounded-md ${
                statusFilter === 'notified'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Notified ({entries.filter(e => e.status === 'notified').length})
            </button>
            <button
              data-testid="filter-converted-button"
              onClick={() => setStatusFilter('converted')}
              className={`px-3 py-1 text-sm rounded-md ${
                statusFilter === 'converted'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Converted ({entries.filter(e => e.status === 'converted').length})
            </button>
          </div>
        </div>
      </div>

      {/* Waitlist Entries */}
      <div className="space-y-3">
        {filteredEntries.length === 0 ? (
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center" data-testid="no-waitlist-entries-message">
            <div className="max-w-md mx-auto">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {statusFilter === 'all' ? 'No Vendors on Waitlist Yet' : 'No Entries Found'}
              </h3>
              <p className="text-gray-600 mb-4">
                {statusFilter === 'all'
                  ? 'When vendors join your waitlist, they\'ll appear here. You can then manage offers and track their status.'
                  : `No entries with status "${statusFilter}". Try selecting a different filter or check back later.`}
              </p>
              {statusFilter === 'all' && (
                <div className="mt-6 p-4 bg-moonbone border border-teal-200 rounded-lg text-left">
                  <p className="text-sm font-medium text-ink mb-2">💡 Helpful Tip</p>
                  <p className="text-sm text-ink">
                    Promote your fair to fill slots faster! Share your fair page with potential vendors to grow your waitlist.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div data-testid="waitlist-entries-list">
            {filteredEntries.map((entry) => (
              <WaitlistEntryRow
                key={entry.id}
                entry={entry}
                onUpdate={handleEntryUpdate}
                onRemove={handleEntryRemove}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6" data-testid="waitlist-pagination">
          <button
            data-testid="waitlist-pagination-previous"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600" data-testid="waitlist-pagination-info">
            Page {page} of {totalPages}
          </span>
          <button
            data-testid="waitlist-pagination-next"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default WaitlistManager;
